/**
 * Delivery Location Selector (Amazon-style modal)
 * – Auto-detects suburb + postcode via IP geolocation
 * – Opens a centered modal for postcode input
 * – "Detect my location" button for IP re-detection
 * – Shared modal used by announcement bar, PDP, and cart drawer
 * – Persists to localStorage and shares with shipping calculator
 */

if (!customElements.get('delivery-location')) {
  class DeliveryLocation extends HTMLElement {
    constructor() {
      super();

      this.STORAGE_KEY = 'ap:delivery_location';
      this.SHIPPING_SHARED_KEY = 'ap:shipping_calculator:shared_v1';

      this.stateFromPostcode = (pc) => {
        const n = parseInt(pc, 10);
        if (n === 2899) return { country: 'Norfolk Island', province: '', short: 'NF' };
        if (n >= 2600 && n <= 2618) return { country: 'Australia', province: 'Australian Capital Territory', short: 'ACT' };
        if (n >= 2900 && n <= 2920) return { country: 'Australia', province: 'Australian Capital Territory', short: 'ACT' };
        if (n >= 800 && n <= 999) return { country: 'Australia', province: 'Northern Territory', short: 'NT' };
        if (n >= 1000 && n <= 2599) return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
        if (n >= 2619 && n <= 2898) return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
        if (n >= 2900 && n <= 2999) return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
        if (n >= 2921 && n <= 2999) return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
        if (n >= 3000 && n <= 3999) return { country: 'Australia', province: 'Victoria', short: 'VIC' };
        if (n >= 8000 && n <= 8999) return { country: 'Australia', province: 'Victoria', short: 'VIC' };
        if (n >= 4000 && n <= 4999) return { country: 'Australia', province: 'Queensland', short: 'QLD' };
        if (n >= 9000 && n <= 9999) return { country: 'Australia', province: 'Queensland', short: 'QLD' };
        if (n >= 5000 && n <= 5999) return { country: 'Australia', province: 'South Australia', short: 'SA' };
        if (n >= 6000 && n <= 6797) return { country: 'Australia', province: 'Western Australia', short: 'WA' };
        if (n === 6798) return { country: 'Christmas Island', province: '', short: 'CX' };
        if (n === 6799) return { country: 'Cocos (Keeling) Islands', province: '', short: 'CC' };
        if (n >= 6800 && n <= 6999) return { country: 'Australia', province: 'Western Australia', short: 'WA' };
        if (n >= 7000 && n <= 7999) return { country: 'Australia', province: 'Tasmania', short: 'TAS' };
        return { country: 'Australia', province: 'New South Wales', short: 'NSW' };
      };

      this.label = this.querySelector('.js-location-label');
      this.toggle = this.querySelector('.js-location-toggle');
    }

    connectedCallback() {
      this.modal = document.querySelector('.js-dl-modal');
      this.bindEvents();
      this.restoreOrDetect();
    }

    bindEvents() {
      // Open modal from announcement bar toggle
      if (this.toggle) {
        this.toggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openModal();
        });
      }

      // Global: any element with js-open-dl-modal opens the modal
      document.addEventListener('click', (e) => {
        if (e.target.closest('.js-open-dl-modal')) {
          e.preventDefault();
          this.openModal();
        }
      });

      if (!this.modal) return;

      // Close buttons
      this.modal.querySelectorAll('.js-dl-modal-close').forEach((btn) => {
        btn.addEventListener('click', () => this.closeModal());
      });

      // Apply postcode
      const applyBtn = this.modal.querySelector('.js-dl-modal-apply');
      const postcodeInput = this.modal.querySelector('.js-dl-modal-postcode');
      const errorEl = this.modal.querySelector('.js-dl-modal-error');

      if (applyBtn) {
        applyBtn.addEventListener('click', () => {
          this.handleModalSubmit(postcodeInput, errorEl, suggestionsEl);
        });
      }

      const suggestionsEl = this.modal.querySelector('.js-dl-suggestions');

      if (postcodeInput) {
        postcodeInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.handleModalSubmit(postcodeInput, errorEl, suggestionsEl);
          }
        });
        postcodeInput.addEventListener('input', async () => {
          errorEl.hidden = true;
          postcodeInput.classList.remove('dl-modal__input--error');

          const pc = postcodeInput.value.trim();
          if (suggestionsEl) {
            suggestionsEl.hidden = true;
            suggestionsEl.innerHTML = '';
          }

          if (/^\d{4}$/.test(pc) && suggestionsEl) {
            const suburbs = await this.fetchSuburbs(pc);
            if (suburbs.length > 0) {
              suggestionsEl.innerHTML = suburbs.map(s =>
                `<button type="button" class="dl-modal__suggestion js-dl-suggestion" data-postcode="${pc}" data-suburb="${s}">${s}</button>`
              ).join('');
              suggestionsEl.hidden = false;
              suggestionsEl.querySelectorAll('.js-dl-suggestion').forEach(btn => {
                btn.addEventListener('click', () => {
                  this.setLocation(btn.dataset.postcode, btn.dataset.suburb);
                  this.closeModal();
                });
              });
            }
          }
        });
      }

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.modal.hidden) {
          this.closeModal();
        }
      });
    }

    openModal() {
      if (!this.modal) return;

      // Move modal to body to escape cart drawer z-index stacking context
      if (this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }

      this.renderSavedAddresses();
      this.modal.hidden = false;
      document.body.style.overflow = 'hidden';
      const input = this.modal.querySelector('.js-dl-modal-postcode');
      if (input) {
        const stored = this.readStorage();
        if (stored && stored.postcode) input.value = stored.postcode;
        setTimeout(() => { input.focus(); input.select(); }, 100);
      }
    }

    renderSavedAddresses() {
      const container = this.modal.querySelector('.js-dl-saved-addrs');
      if (!container) return;

      const addrs = window.__AP_CUSTOMER_ADDRS__;
      if (!addrs || !addrs.length) { container.hidden = true; return; }

      const stored = this.readStorage();
      const currentPC = stored && stored.postcode;

      const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

      container.innerHTML = `
        <p class="dl-modal__saved-label">Your saved addresses</p>
        <div class="dl-modal__addr-list">
          ${addrs.map(addr => `
            <button type="button" class="dl-modal__addr-btn js-dl-addr-btn${addr.postcode === currentPC ? ' dl-modal__addr-btn--active' : ''}"
              data-postcode="${esc(addr.postcode)}" data-suburb="${esc(addr.suburb)}">
              <span class="dl-modal__addr-suburb">${esc(addr.suburb)} <strong>${esc(addr.postcode)}</strong></span>
              <span class="dl-modal__addr-street">${esc(addr.address1)}</span>
              ${addr.isDefault ? '<span class="dl-modal__addr-badge">Default</span>' : ''}
            </button>
          `).join('')}
        </div>
        <div class="dl-modal__divider"><span>or enter a postcode</span></div>
      `;
      container.hidden = false;

      container.querySelectorAll('.js-dl-addr-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.setLocation(btn.dataset.postcode, btn.dataset.suburb);
          this.closeModal();
        });
      });
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.body.style.overflow = '';
    }

    async handleModalSubmit(input, errorEl, suggestionsEl) {
      const pc = (input.value || '').trim();
      if (/^\d{4}$/.test(pc)) {
        errorEl.hidden = true;
        input.classList.remove('dl-modal__input--error');
        // Use first visible suggestion if available, otherwise fetch
        const firstBtn = suggestionsEl && suggestionsEl.querySelector('.js-dl-suggestion');
        let suburb;
        if (firstBtn) {
          suburb = firstBtn.dataset.suburb;
        } else {
          const suburbs = await this.fetchSuburbs(pc);
          suburb = suburbs[0] || this.stateFromPostcode(pc).short;
        }
        this.setLocation(pc, suburb);
        this.closeModal();
      } else {
        errorEl.hidden = false;
        input.classList.add('dl-modal__input--error');
      }
    }

    restoreOrDetect() {
      const stored = this.readStorage();
      if (stored && stored.postcode) {
        // User has previously set a location — restore it
        const suburb = stored.suburb || this.stateFromPostcode(stored.postcode).short;
        this.applyLocation(stored.postcode, suburb);
      } else if (window.__AP_CUSTOMER_ADDR__?.postcode) {
        // Logged-in customer with a saved AU address — use it as default
        const { postcode, suburb } = window.__AP_CUSTOMER_ADDR__;
        this.setLocation(postcode, suburb || this.stateFromPostcode(postcode).short);
      }
      // Otherwise: no auto-detection — user sets their location manually
    }

    async fetchSuburbs(postcode) {
      try {
        const res = await fetch(`https://api.zippopotam.us/au/${postcode}`, { signal: AbortSignal.timeout(3000) });
        if (!res.ok) throw new Error();
        const data = await res.json();
        return (data.places || []).map(p => p['place name']);
      } catch (e) {
        return [];
      }
    }

    setLocation(postcode, suburb) {
      this.applyLocation(postcode, suburb);
      this.persistStorage(postcode, suburb, null);
      this.syncToShippingCalculator(postcode);
      this.broadcastUpdate(postcode, suburb);
    }

    applyLocation(postcode, suburb) {
      if (this.label) {
        this.label.textContent = `${suburb} ${postcode}`;
      }
    }

    // Notify PDP and cart drawer components to update
    broadcastUpdate(postcode, suburb) {
      document.dispatchEvent(new CustomEvent('delivery-location:changed', {
        detail: { postcode, suburb }
      }));
    }

    syncToShippingCalculator(postcode) {
      const loc = this.stateFromPostcode(postcode);
      try {
        localStorage.setItem(this.SHIPPING_SHARED_KEY, JSON.stringify({
          country: loc.country, province: loc.province, zip: postcode, updatedAt: Date.now(),
        }));
      } catch (e) {}
    }

    readStorage() {
      try {
        const raw = localStorage.getItem(this.STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    }

    persistStorage(postcode, suburb, estimate) {
      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
          postcode, suburb, estimate, updatedAt: Date.now(),
        }));
      } catch (e) {}
    }
  }

  customElements.define('delivery-location', DeliveryLocation);
}

